import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiGetAll, apiPost } from 'api/service';
import { cancelScan, deleteScan, listApiEndpoints, runApiSecurityAudit } from 'api/features';
import { useFuzzScanTarget } from 'context/FuzzScanTargetContext';
import {
  alpha, Alert, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Drawer, IconButton,
  InputAdornment, LinearProgress, Menu, MenuItem, Pagination, Snackbar, Stack, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, TextField, Typography
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { buildScanRequests, DEFAULT_OPTIONS } from './webScanLogic';
import { CHECKS, auditBody, endpointKey, groupIssues, issuesByEndpoint, latestAudit, normalizeEndpoint } from './apiSecurityLogic';
import { GREEN, LIVE, MONO, SEV, SEV_COLOR, STATUS, ago, th, took } from './scanUi';

const PAGE_SIZE = 15;
const API_TYPES = new Set(['api_active_security', 'api_openapi']);
const METHOD_COLOR = { GET: '#34d399', POST: '#38bdf8', PUT: '#fcd34d', PATCH: '#fcd34d', DELETE: '#fca5a5' };
const methodChip = (m) => <Chip size="small" label={m} sx={{ minWidth: 62, fontWeight: 700, fontSize: 11, bgcolor: alpha(METHOD_COLOR[m] || '#94a3b8', 0.16), color: METHOD_COLOR[m] || '#94a3b8' }} />;
const typeLabel = (t) => (t === 'api_openapi' ? 'OpenAPI spec' : 'Active audit');

export default function ApiSecurityPage() {
  const { currentTargetId, targets, setCurrentTargetId } = useFuzzScanTarget();
  const userTarget = useMemo(() => (targets || []).find((t) => String(t.id) === String(currentTargetId)) || null, [targets, currentTargetId]);

  const [endpoints, setEndpoints] = useState([]);
  const [scans, setScans] = useState([]);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
  const toast = useCallback((severity, message) => setSnack({ open: true, severity, message }), []);

  const [form, setForm] = useState({ baseUrl: '', spec: '/openapi.json', authHeader: 'Authorization', authValue: '', timeout: 10, burst: 8 });
  const [advanced, setAdvanced] = useState(false);
  const [view, setView] = useState('endpoints');
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [openKey, setOpenKey] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [targetMenu, setTargetMenu] = useState(null);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // The base URL starts from the target's address.
  useEffect(() => {
    const v = userTarget?.target?.value || '';
    setForm((f) => ({ ...f, baseUrl: v ? (/^https?:\/\//i.test(v) ? v : `https://${v}`) : '' }));
  }, [userTarget?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!currentTargetId) { setEndpoints([]); setScans([]); setFindings([]); setLoading(false); return; }
    try {
      const [e, s, f] = await Promise.all([
        listApiEndpoints({ user_target: currentTargetId }),
        apiGet(`/api/scans/?user_target=${currentTargetId}`),
        apiGetAll(`/api/findings/?user_target=${currentTargetId}&scan_type=api_active_security,api_openapi`)
      ]);
      setEndpoints((Array.isArray(e) ? e : e.results || []).map(normalizeEndpoint));
      setScans((s.results || s || []).filter((x) => API_TYPES.has(x.scan_type)));
      setFindings(f || []);
    } catch (err) { setError(err?.message || 'Failed to load API data.'); }
    finally { setLoading(false); }
  }, [currentTargetId]);
  useEffect(() => { setLoading(true); load(); setPage(1); setOpenKey(null); }, [load]);

  const live = useMemo(() => scans.filter((s) => LIVE.has(s.status)), [scans]);
  useEffect(() => {
    if (!live.length || !currentTargetId) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [live.length, currentTargetId, load]);

  // What the newest finished audit found.
  const audit = useMemo(() => latestAudit(scans), [scans]);
  const auditFindings = useMemo(() => (audit ? findings.filter((f) => String(f.scan) === String(audit.id)) : []), [audit, findings]);
  const issueMap = useMemo(() => issuesByEndpoint(auditFindings), [auditFindings]);
  const groups = useMemo(() => groupIssues(auditFindings), [auditFindings]);
  const issuesOf = (ep) => (issueMap[endpointKey(ep.method, ep.path)] || []).length;

  const counts = useMemo(() => ({
    all: endpoints.length,
    issues: endpoints.filter((e) => issuesOf(e) > 0).length,
    noAuth: endpoints.filter((e) => e.auth === 'none').length,
    untested: endpoints.filter((e) => !e.lastTested).length,
    noRate: endpoints.filter((e) => !e.rateLimit).length
  }), [endpoints, issueMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = query.trim().toLowerCase();
  const visible = endpoints.filter((e) => (q ? `${e.method} ${e.path}`.toLowerCase().includes(q) : true)
    && (filter === 'all' || (filter === 'issues' && issuesOf(e) > 0) || (filter === 'noAuth' && e.auth === 'none') || (filter === 'untested' && !e.lastTested)))
    .sort((a, b) => issuesOf(b) - issuesOf(a) || Number(a.auth !== 'none') - Number(b.auth !== 'none'));
  const paged = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const open = endpoints.find((e) => endpointKey(e.method, e.path) === openKey) || null;
  const openIssues = open ? issueMap[openKey] || [] : [];

  const act = async (fn, okMessage) => {
    setBusy(true); setError('');
    try { await fn(); toast('success', okMessage); await load(); }
    catch (e) { setError(e?.message || 'That did not work.'); }
    finally { setBusy(false); }
  };
  const runAudit = () => act(() => runApiSecurityAudit(auditBody({ userTargetId: currentTargetId, baseUrl: form.baseUrl, timeout: form.timeout, burst: form.burst, authHeader: form.authHeader, authValue: form.authValue })), 'API audit queued.');
  const readSpec = () => act(async () => {
    const [req] = buildScanRequests({ scanType: 'api_openapi', options: { ...DEFAULT_OPTIONS, openapiPath: form.spec || '/openapi.json' }, authProfileId: '', userTargetId: userTarget.id, targets: [{ id: 'root', value: form.baseUrl, label: form.baseUrl }] });
    await apiPost('/api/scans/', req);
  }, 'Reading the OpenAPI spec.');
  const doConfirm = async () => {
    const { type, scan } = confirm;
    await act(async () => { if (type === 'cancel') await cancelScan(scan.id); else await deleteScan(scan.id); }, type === 'cancel' ? 'Scan stopped.' : 'Scan deleted.');
    setConfirm(null);
  };

  if (!currentTargetId) return <Box className="fuzzscan-page" sx={{ p: { xs: 2, md: 3 } }}><Alert severity="info">Select a target to test its API.</Alert></Box>;

  const stepLabel = { fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, mb: 1 };
  const card = { border: 1, borderColor: 'divider', borderRadius: 4, bgcolor: 'background.paper' };
  const mono = { sx: { fontFamily: MONO, fontSize: 13 } };
  const kpis = [['Endpoints', counts.all, 'inherit'], ['With issues', counts.issues, SEV_COLOR.critical], ['Total issues', auditFindings.length, SEV_COLOR.high], ['No sign-in', counts.noAuth, SEV_COLOR.medium], ['No rate limit', counts.noRate, SEV_COLOR.low]];

  return (
    <Box className="fuzzscan-page" sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
      <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>API security</Typography>
          <Typography variant="body2" color="text.secondary">Test the APIs of <Box component="span" sx={{ fontFamily: MONO, color: 'text.primary' }}>{userTarget?.target?.value || userTarget?.name}</Box> against the OWASP API Top 10</Typography>
        </Box>
        <Button variant="outlined" onClick={(e) => setTargetMenu(e.currentTarget)} sx={{ minHeight: 44, borderColor: 'divider', color: 'text.primary', fontWeight: 600 }}>Switch target</Button>
        <Menu anchorEl={targetMenu} open={!!targetMenu} onClose={() => setTargetMenu(null)}>
          {(targets || []).filter((t) => t.target?.type !== 'mobile').map((t) => (
            <MenuItem key={t.id} selected={String(t.id) === String(currentTargetId)} onClick={() => { setCurrentTargetId(String(t.id)); setTargetMenu(null); }}>{t.name || t.target?.name || t.target?.value}</MenuItem>
          ))}
        </Menu>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ ...card, p: 3, mb: 3, borderRadius: 4.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} gap={3}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={stepLabel}>1 · Which API</Typography>
            <TextField fullWidth size="small" label="Base URL" value={form.baseUrl} onChange={(e) => setField('baseUrl', e.target.value)} placeholder="https://api.example.com" InputProps={mono} />
            <TextField fullWidth size="small" label="OpenAPI spec (optional)" value={form.spec} onChange={(e) => setField('spec', e.target.value)} sx={{ mt: 2 }} InputProps={mono} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={stepLabel}>2 · Sign in (optional)</Typography>
            <Stack direction="row" gap={1.5}>
              <TextField size="small" label="Header" value={form.authHeader} onChange={(e) => setField('authHeader', e.target.value)} sx={{ flex: 1 }} InputProps={mono} />
              <TextField size="small" label="Value" type="password" autoComplete="off" value={form.authValue} onChange={(e) => setField('authValue', e.target.value)} placeholder="Bearer eyJ…" sx={{ flex: 2 }} InputProps={mono} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>The value is sent with the audit and kept in that scan&apos;s settings.</Typography>
          </Box>
        </Stack>
        <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} sx={{ borderTop: 1, borderColor: 'divider', pt: 2.5 }}>
          <Button size="small" onClick={() => setAdvanced((v) => !v)} sx={{ color: GREEN, fontWeight: 700 }}>{advanced ? 'Hide advanced options' : `Advanced options · timeout ${form.timeout} s · rate-limit test ${form.burst} requests`}</Button>
          <Stack direction="row" gap={1.2} flexWrap="wrap">
            <Button variant="outlined" disabled={busy || !form.baseUrl.trim()} onClick={readSpec} sx={{ height: 48, px: 3, borderRadius: 3, borderColor: 'divider', color: 'text.primary', fontWeight: 600 }}>Read the OpenAPI spec</Button>
            <Button variant="contained" disabled={busy} onClick={runAudit} sx={{ height: 48, px: 4, fontSize: 15, borderRadius: 3, bgcolor: GREEN, color: '#06220F', fontWeight: 700, '&:hover': { bgcolor: '#16a34a' } }}>Run active audit</Button>
          </Stack>
        </Stack>
        <Collapse in={advanced} unmountOnExit>
          <Stack direction="row" gap={2}>
            <TextField size="small" type="number" label="Timeout (seconds)" value={form.timeout} onChange={(e) => setField('timeout', e.target.value)} sx={{ width: 200 }} />
            <TextField size="small" type="number" label="Rate-limit test requests" value={form.burst} onChange={(e) => setField('burst', e.target.value)} sx={{ width: 220 }} />
          </Stack>
        </Collapse>
      </Box>

      {live.map((s) => (
        <Box key={s.id} sx={{ ...card, borderColor: GREEN, p: 2, mb: 2.5 }}>
          <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2}>
            <Box><Typography sx={{ fontWeight: 600 }}>{typeLabel(s.scan_type)} running</Typography><Typography variant="body2" color="text.secondary">{s.progress_message || 'Working…'} · {Math.round(Number(s.progress ?? 0))}%</Typography></Box>
            <Stack direction="row" alignItems="center" gap={1.5}>
              <LinearProgress variant="determinate" value={Number(s.progress ?? 0)} sx={{ width: 200, height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: GREEN } }} />
              <Button size="small" color="error" variant="outlined" onClick={() => setConfirm({ type: 'cancel', scan: s })}>Stop</Button>
            </Stack>
          </Stack>
        </Box>
      ))}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
        {kpis.map(([label, value, color]) => (
          <Box key={label} sx={{ ...card, borderTop: `3px solid ${color === 'inherit' ? '#64748b' : color}`, borderRadius: 3.5, p: 2 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 800, fontFamily: MONO, color, mt: 0.5 }}>{value}</Typography>
          </Box>
        ))}
      </Box>

      <Tabs value={view} onChange={(_, v) => setView(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 }, '& .MuiTabs-indicator': { bgcolor: GREEN, height: 3 } }}>
        <Tab value="endpoints" label={`Endpoints ${counts.all}`} />
        <Tab value="issues" label={`Issues by problem ${groups.length}`} />
        <Tab value="audits" label={`Audits ${scans.length}`} />
      </Tabs>

      {view === 'endpoints' && (
        <Box sx={{ ...card, overflow: 'hidden' }}>
          <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={1.5} sx={{ p: 2.5 }}>
            <Stack direction="row" gap={0.8} flexWrap="wrap">
              {[['all', `All ${counts.all}`], ['issues', `With issues ${counts.issues}`], ['noAuth', `No sign-in ${counts.noAuth}`], ['untested', `Not tested ${counts.untested}`]].map(([id, label]) => (
                <Chip key={id} clickable label={label} onClick={() => { setFilter(id); setPage(1); }}
                  sx={{ fontWeight: 600, border: 1, borderColor: filter === id ? alpha(GREEN, 0.5) : 'divider', bgcolor: filter === id ? alpha(GREEN, 0.16) : 'transparent', color: filter === id ? GREEN : 'text.secondary' }} />
              ))}
            </Stack>
            <TextField size="small" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} placeholder="Search endpoints" sx={{ minWidth: 240 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          </Stack>
          {loading && <LinearProgress />}
          <TableContainer>
            <Table sx={{ minWidth: 860 }}>
              <TableHead><TableRow>
                <TableCell sx={{ ...th, pl: 2.5 }}>Endpoint</TableCell><TableCell sx={th}>Sign-in</TableCell><TableCell sx={th}>Rate limit</TableCell>
                <TableCell sx={th}>Issues</TableCell><TableCell sx={th}>Last tested</TableCell><TableCell sx={th} />
              </TableRow></TableHead>
              <TableBody>
                {paged.length === 0 && <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>{endpoints.length ? 'No endpoint matches.' : 'No endpoints yet. Read the OpenAPI spec or run an active audit to find them.'}</Typography></TableCell></TableRow>}
                {paged.map((e) => {
                  const n = issuesOf(e);
                  return (
                    <TableRow key={e.id} hover onClick={() => setOpenKey(endpointKey(e.method, e.path))} sx={{ cursor: 'pointer' }}>
                      <TableCell sx={{ pl: 2.5, py: 1.6 }}><Stack direction="row" alignItems="center" gap={1.5}>{methodChip(e.method)}<Typography sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 600 }}>{e.path}</Typography></Stack></TableCell>
                      <TableCell><Chip size="small" label={e.auth} sx={{ fontWeight: 600, bgcolor: alpha(e.auth === 'none' ? '#ef4444' : '#94a3b8', 0.16), color: e.auth === 'none' ? '#fca5a5' : '#cbd5e1' }} /></TableCell>
                      <TableCell sx={{ fontSize: 13, color: !e.lastTested ? 'text.disabled' : e.rateLimit ? '#4ade80' : '#fca5a5' }}>{!e.lastTested ? '—' : e.rateLimit ? 'Limited' : 'No limit'}</TableCell>
                      <TableCell><Box component="span" sx={{ fontFamily: MONO, fontWeight: 700, color: n ? SEV_COLOR.critical : 'text.disabled' }}>{n}</Box></TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{e.lastTested ? ago(e.lastTested) : 'Not tested'}</TableCell>
                      <TableCell align="right" sx={{ pr: 2 }}><Button size="small" variant="outlined" sx={{ borderColor: 'divider', color: 'text.primary', fontWeight: 600 }}>Details</Button></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {visible.length > PAGE_SIZE && <Stack alignItems="center" sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}><Pagination count={Math.ceil(visible.length / PAGE_SIZE)} page={page} onChange={(_, v) => setPage(v)} size="small" /></Stack>}
        </Box>
      )}

      {view === 'issues' && (
        <Stack gap={2}>
          {!audit && <Alert severity="info">Run an active audit to see what it finds, grouped by problem.</Alert>}
          {audit && groups.length === 0 && <Alert severity="success">The last audit ({ago(audit.finished_at || audit.created_at)}) found no issues.</Alert>}
          {groups.map((g) => (
            <Box key={g.check} sx={{ ...card, overflow: 'hidden' }}>
              <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={1.5} sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" gap={1.8}>
                  <Chip size="small" label={g.severity.toUpperCase()} sx={{ fontWeight: 700, fontSize: 11, bgcolor: alpha(SEV_COLOR[g.severity] || SEV_COLOR.info, 0.16), color: SEV_COLOR[g.severity] || SEV_COLOR.info }} />
                  <Box><Typography sx={{ fontWeight: 700, fontSize: 16 }}>{g.title}</Typography><Typography variant="body2" color="text.secondary">{g.why}</Typography></Box>
                </Stack>
                <Typography sx={{ fontFamily: MONO, fontSize: 13, color: 'text.secondary' }}>{g.items.length} endpoint{g.items.length === 1 ? '' : 's'}</Typography>
              </Stack>
              {g.items.slice(0, 8).map((it, i) => (
                <Stack key={`${it.method}${it.path}${i}`} direction="row" alignItems="center" gap={1.5} onClick={() => setOpenKey(endpointKey(it.method, it.path))}
                  sx={{ px: 2.5, py: 1.3, borderTop: 1, borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                  {methodChip(it.method)}<Typography sx={{ fontFamily: MONO, fontSize: 13, flex: 1 }}>{it.path}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 360 }}>{String(it.finding.evidence || '').split('\n')[0]}</Typography>
                </Stack>
              ))}
              {g.items.length > 8 && <Typography variant="caption" color="text.secondary" sx={{ px: 2.5, py: 1, display: 'block', borderTop: 1, borderColor: 'divider' }}>+{g.items.length - 8} more</Typography>}
              {g.fix && <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, py: 1.4, borderTop: 1, borderColor: 'divider' }}>How to fix: {g.fix}</Typography>}
            </Box>
          ))}
        </Stack>
      )}

      {view === 'audits' && (
        <Box sx={{ ...card, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead><TableRow><TableCell sx={{ ...th, pl: 2.5 }}>Scan</TableCell><TableCell sx={th}>Status</TableCell><TableCell sx={th}>Result</TableCell><TableCell sx={th}>Started</TableCell><TableCell sx={th} align="right">Actions</TableCell></TableRow></TableHead>
              <TableBody>
                {scans.length === 0 && <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>No audits yet.</Typography></TableCell></TableRow>}
                {scans.map((s) => {
                  const st = STATUS[s.status] || STATUS.pending;
                  const sum = s.options?.api_active_summary;
                  const n = findings.filter((f) => String(f.scan) === String(s.id)).length;
                  return (
                    <TableRow key={s.id} hover>
                      <TableCell sx={{ pl: 2.5, fontWeight: 600 }}>{typeLabel(s.scan_type)}{s.error_message && <Typography variant="caption" color="error" display="block">{s.error_message.slice(0, 80)}</Typography>}</TableCell>
                      <TableCell><Chip size="small" label={st.label} sx={{ fontWeight: 700, fontSize: 11, bgcolor: alpha(st.color, 0.16), color: st.color }} /></TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{LIVE.has(s.status) ? `${Math.round(Number(s.progress ?? 0))}%` : sum ? `${sum.checked_endpoints} endpoints checked, ${n} issue${n === 1 ? '' : 's'}` : `${n} finding${n === 1 ? '' : 's'}`}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{ago(s.started_at || s.created_at)}{took(s.started_at, s.finished_at) ? ` · ${took(s.started_at, s.finished_at)}` : ''}</TableCell>
                      <TableCell align="right" sx={{ pr: 2 }}>
                        {LIVE.has(s.status) ? <Button size="small" color="error" onClick={() => setConfirm({ type: 'cancel', scan: s })}>Stop</Button> : <Button size="small" color="error" onClick={() => setConfirm({ type: 'delete', scan: s })}>Delete</Button>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Drawer anchor="right" open={!!open} onClose={() => setOpenKey(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, p: 3 } }}>
        {open && (
          <Stack spacing={2.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" gap={1.5}>{methodChip(open.method)}<Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 18, wordBreak: 'break-all' }}>{open.path}</Typography></Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Sign-in: {open.auth} · Rate limit: {!open.lastTested ? 'not tested' : open.rateLimit ? 'yes' : 'none found'} · Last tested {open.lastTested ? ago(open.lastTested) : 'never'}</Typography>
              </Box>
              <IconButton aria-label="Close" onClick={() => setOpenKey(null)}><CloseIcon /></IconButton>
            </Stack>
            <Typography sx={{ fontWeight: 700 }}>Issues from the last audit ({openIssues.length})</Typography>
            {openIssues.length === 0 && <Typography variant="body2" color="text.secondary">{open.lastTested ? 'No issues found on this endpoint.' : 'This endpoint has not been tested yet. Run an active audit.'}</Typography>}
            {[...openIssues].sort((a, b) => SEV.indexOf(a.severity) - SEV.indexOf(b.severity)).map((f) => {
              const info = CHECKS[f.raw_data?.check];
              return (
                <Box key={f.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 3, p: 2 }}>
                  <Stack direction="row" gap={1.2} alignItems="center" sx={{ mb: 0.8 }}>
                    <Chip size="small" label={f.severity} sx={{ textTransform: 'capitalize', fontWeight: 700, bgcolor: alpha(SEV_COLOR[f.severity] || SEV_COLOR.info, 0.16), color: SEV_COLOR[f.severity] || SEV_COLOR.info }} />
                    <Typography sx={{ fontWeight: 600 }}>{info?.title || f.title}</Typography>
                  </Stack>
                  {f.evidence && <Typography variant="body2" color="text.secondary" sx={{ fontFamily: MONO, fontSize: 12, wordBreak: 'break-all' }}>{f.evidence}</Typography>}
                  {info?.fix && <Typography variant="body2" sx={{ mt: 1 }}>How to fix: {info.fix}</Typography>}
                </Box>
              );
            })}
          </Stack>
        )}
      </Drawer>

      <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)}>
        <DialogTitle>{confirm?.type === 'cancel' ? 'Stop this scan?' : 'Delete this scan?'}</DialogTitle>
        <DialogContent><DialogContentText>{confirm?.type === 'cancel' ? 'The scan stops and what it found so far is kept.' : 'The scan and its findings are removed. The endpoint list stays. If it is still running, stop it first.'}</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color={confirm?.type === 'cancel' ? 'warning' : 'error'} onClick={doConfirm} disabled={busy}>{confirm?.type === 'cancel' ? 'Stop' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((p) => ({ ...p, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
